// Client-side settings (Svelte 5 runes), persisted to localStorage: the pack-opening animation
// toggle plus audio preferences (SFX on/off + volume, background-audio volume). The actual
// background audio is NOT here, it's player uploads stored locally in IndexedDB (audio.svelte.ts).

const KEY = "df.settings";

/** A concrete render tier. "auto" (below) resolves to one of these at runtime via the perf monitor. */
export type QualityTier = "low" | "medium" | "high";
export type RenderQuality = "auto" | QualityTier;

/** Render resolution ceiling (device-pixel-ratio) for the 3D duel board, by tier. Low = native 1×
 *  (the big GPU win on weak machines), medium = the tuned default, high = crisp for strong GPUs. */
export const qualityDpr = (t: QualityTier): number => (t === "low" ? 1 : t === "high" ? 2 : 1.5);
/** Frame-rate cap for the continuously-rendering scenes (overworld + 3D duel board), by tier. Only
 *  Low caps to 30fps — the thermal lever for weak machines (30 roughly halves GPU duty cycle, and a
 *  top-down walker / card game reads fine at 30). Medium/High run at 60. */
export const qualityMaxFps = (t: QualityTier): number => (t === "low" ? 30 : 60);

/** The tier actually in force: the user's explicit pick, or the perf monitor's choice under "auto". */
export function effectiveTier(): QualityTier {
  return settings.renderQuality === "auto" ? settings.autoTier : settings.renderQuality;
}

interface Persisted {
  packAnimations?: boolean;
  sfxEnabled?: boolean;
  sfxVolume?: number;
  audioVolume?: number;
  performanceMode?: boolean;
  reducedEffects?: boolean;
  animatedBackdrop?: boolean;
  renderQuality?: RenderQuality;
  duelSpeed?: number;
}

function load(): Persisted {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? "{}");
  } catch {
    return {};
  }
}

const init = load();
const clamp01 = (n: number | undefined, fallback: number): number =>
  typeof n === "number" && n >= 0 && n <= 1 ? n : fallback;

export const settings = $state({
  /** Animate pack opening (sequential card flips + rarity bursts). Off → instant grid. */
  packAnimations: init.packAnimations !== false,
  /** Play duel sound-effects (card placements, attacks, etc.). On by default. */
  sfxEnabled: init.sfxEnabled !== false,
  /** SFX level, 0–1. */
  sfxVolume: clamp01(init.sfxVolume, 0.6),
  /** Background-audio level, 0–1 (only matters once the player uploads audio). */
  audioVolume: clamp01(init.audioVolume, 0.5),
  /** Performance mode: force the flat 2D duel board (skip the 3D WebGPU arena). The heaviest single
   *  perf switch — the master lever for very weak machines. */
  performanceMode: init.performanceMode === true,
  /** In-app "reduce motion": kills sparks, screen-shake, bloom flourishes, chain-ring spin and the
   *  ambient backdrop drift. ORed with the OS prefers-reduced-motion, so it can only ADD reduction. */
  reducedEffects: init.reducedEffects === true,
  /** Draw the animated cyber backdrop behind menus/duel. Off → a static (cheaper) backdrop. */
  animatedBackdrop: init.animatedBackdrop !== false,
  /** Render-quality preference. "auto" (default) lets the perf monitor pick a tier from the device +
   *  live frame rate; a concrete tier pins it. See qualityDpr / qualityDuelFps / qualityWorldFps. */
  renderQuality: (["auto", "low", "medium", "high"] as const).includes(init.renderQuality as RenderQuality)
    ? (init.renderQuality as RenderQuality)
    : "auto",
  /** The tier "auto" currently resolves to (set by initPerf/the FPS monitor, NOT persisted — it's
   *  re-derived from the live device each session). Ignored unless renderQuality === "auto". */
  autoTier: "medium" as QualityTier,
  /** Duel animation speed multiplier (MD-style): 1 = authored pacing, higher = snappier. Drives the
   *  playback beats, the 2D GSAP timeline scale, and the 3D tween clock together. */
  duelSpeed: typeof init.duelSpeed === "number" && [0.75, 1, 1.5, 2].includes(init.duelSpeed) ? init.duelSpeed : 1,
});

function persist(): void {
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({
        packAnimations: settings.packAnimations,
        sfxEnabled: settings.sfxEnabled,
        sfxVolume: settings.sfxVolume,
        audioVolume: settings.audioVolume,
        performanceMode: settings.performanceMode,
        reducedEffects: settings.reducedEffects,
        animatedBackdrop: settings.animatedBackdrop,
        renderQuality: settings.renderQuality,
        duelSpeed: settings.duelSpeed,
      }),
    );
  } catch {
    /* private mode, settings just won't persist */
  }
}

export function setPackAnimations(on: boolean): void {
  settings.packAnimations = on;
  persist();
}

/** Cycle the duel animation speed ×1 → ×1.5 → ×2 → ×0.75 → ×1 (the MD-style speed knob). */
export function cycleDuelSpeed(): void {
  const order = [1, 1.5, 2, 0.75];
  settings.duelSpeed = order[(order.indexOf(settings.duelSpeed) + 1) % order.length]!;
  persist();
}

export function setSfxEnabled(on: boolean): void {
  settings.sfxEnabled = on;
  persist();
}

export function setSfxVolume(v: number): void {
  settings.sfxVolume = v;
  persist();
}

export function setAudioVolume(v: number): void {
  settings.audioVolume = v;
  persist();
}

export function setPerformanceMode(on: boolean): void {
  settings.performanceMode = on;
  persist();
}

export function setReducedEffects(on: boolean): void {
  settings.reducedEffects = on;
  persist();
}

export function setAnimatedBackdrop(on: boolean): void {
  settings.animatedBackdrop = on;
  persist();
}

export function setRenderQuality(q: RenderQuality): void {
  settings.renderQuality = q;
  persist();
}

/** Set the resolved "auto" tier (perf monitor only — not persisted). */
export function setAutoTier(t: QualityTier): void {
  settings.autoTier = t;
}
